import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, BellPlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const urlB64ToUint8 = (b64) => {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

const timeAgo = (isoStr) => {
  if (!isoStr) return "";
  const mins = Math.max(0, Math.round((Date.now() - new Date(isoStr).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const load = () => api.get("/notifications").then((r) => setData(r.data)).catch((e) => console.debug("Notifications load failed:", e?.message || e));
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const [pushOn, setPushOn] = useState(false);

  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    navigator.serviceWorker.ready
      .then((r) => r.pushManager.getSubscription())
      .then((s) => setPushOn(Boolean(s)))
      .catch(() => {});
  }, []);

  const enablePush = async () => {
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        toast.error("This browser doesn't support phone alerts — try Chrome or Safari");
        return;
      }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Notifications were blocked — allow them in your browser settings"); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-key");
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8(data.key) });
      await api.post("/push/subscribe", { subscription: sub.toJSON() });
      setPushOn(true);
      toast.success("Phone alerts on — you'll get notified even when the app is closed");
    } catch (e) {
      toast.error("Couldn't switch on phone alerts — " + (e?.message || "try again"));
    }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read");
      setData((d) => ({ ...d, unread: 0, notifications: d.notifications.map((n) => ({ ...n, read: true })) }));
    } catch (e) {
      console.debug("Notifications mark-read failed:", e?.message || e);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)} data-testid="notifications-bell" aria-label="Notifications"
        className="relative h-11 w-11 grid place-items-center rounded-lg border border-slate-300 text-[#0F2547] hover:border-[#005EB8] hover:text-[#005EB8] transition-colors">
        <Bell className="h-4 w-4" />
        {data.unread > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D5281B] text-white text-[10px] font-black grid place-items-center" data-testid="notifications-badge">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-12 w-80 max-w-[85vw] bg-white rounded-xl border border-slate-200 shadow-xl z-50 overflow-hidden" data-testid="notifications-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <p className="text-sm font-bold text-[#0F2547]">Notifications</p>
            <button onClick={enablePush} data-testid="notifications-enable-push"
              title="Get notifications on this phone, even when the app is closed"
              className={`text-[11px] font-bold inline-flex items-center gap-1 ${pushOn ? "text-[#007F3B]" : "text-[#005EB8] hover:underline"}`}>
              <BellPlus className="h-3.5 w-3.5" /> {pushOn ? "Alerts on" : "Phone alerts"}
            </button>
            <button onClick={markAllRead} data-testid="notifications-read-all"
              className="text-[11px] font-bold text-[#005EB8] hover:underline inline-flex items-center gap-1">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-slate-50">
            {data.notifications.length === 0 ? (
              <p className="text-xs text-slate-400 px-4 py-6 text-center" data-testid="notifications-empty">All caught up — nothing new.</p>
            ) : (
              data.notifications.map((n) => (
                <button key={n.id} onClick={() => { setOpen(false); if (n.link) navigate(n.link); }}
                  data-testid={`notification-${n.id}`}
                  className={`w-full text-left px-4 py-3 hover:bg-sky-50 transition-colors ${n.read ? "opacity-60" : ""}`}>
                  <p className="text-xs font-bold text-[#0F2547] flex items-center gap-2">
                    {!n.read && <span className="h-2 w-2 rounded-full bg-[#005EB8] shrink-0" />}
                    {n.title}
                  </p>
                  {n.body && <p className="text-[11px] text-slate-500 mt-0.5">{n.body}</p>}
                  {n.created_at && <p className="text-[10px] text-slate-400 mt-0.5">{timeAgo(n.created_at)}</p>}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
